import { AfterViewInit, Component, Inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { PlantStatusService } from './plant-status.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import type { ApexOptions } from 'apexcharts';

@Component({
  selector: 'app-plant-status-graph',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatMenuModule,
    TranslatePipe
  ],
  templateUrl: './plant-status-graph.html',
  styleUrl: './plant-status-graph.scss'
})
export class PlantStatusGraph implements AfterViewInit, OnDestroy {

  private chart: any;
  private langSub?: Subscription;
  private readonly isBrowser: boolean;

  constructor(
    private plantStatusService: PlantStatusService,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.isBrowser) return;

    await this.initChart();

    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.updateTranslations();
    });
  }

  private async initChart(): Promise<void> {
    const ApexCharts = (await import('apexcharts')).default;

    const chartElement =
      document.getElementById('plant_status_graph');

    if (!chartElement) {
      return;
    }

    const data = this.plantStatusService.getData();

    this.chart = new ApexCharts(
      chartElement,
      this.getOptions(data)
    );

    await this.chart.render();
  }

  private getOptions(data: number[]): ApexOptions {
    return {
      series: data,

      chart: {
        type: 'donut',
        height: 300,

        animations: {
          enabled: true,
          speed: 850,

          animateGradually: {
            enabled: true,
            delay: 120
          },

          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        },

        toolbar: {
          show: false
        },

        sparkline: {
          enabled: false
        }
      },

      labels: this.getLabels(),

      colors: [
        '#80CBC4',
        '#DCE775',
        '#94a3b8'
      ],

      legend: {
        position: 'bottom',
        horizontalAlign: 'center',

        labels: {
          colors: '#919aa3'
        },

        itemMargin: {
          horizontal: 12,
          vertical: 12
        },

        formatter: (
          label: string,
          opts?: any
        ): string => {
          const value =
            opts?.w?.globals?.series?.[opts.seriesIndex];

          return `${label} ${value ?? 0}`;
        }
      },

      dataLabels: {
        enabled: false
      },

      stroke: {
        width: 0
      },

      fill: {
        type: 'gradient',

        gradient: {
          shade: 'light',
          type: 'vertical',
          opacityFrom: 0.95,
          opacityTo: 0.78
        }
      },

      tooltip: {
        y: {
          formatter: (value: number): string => {
            const plantLabel =
              value === 1
                ? this.translate.instant('Plant')
                : this.translate.instant('Plants');

            return `${value} ${plantLabel}`;
          }
        }
      },

      plotOptions: {
        pie: {
          donut: {
            size: '72%',

            labels: {
              show: true,

              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 800,
                color: '#172033'
              },

              total: {
                show: true,
                label: this.translate.instant('Total_Plants'),
                fontSize: '13px',
                color: '#919aa3',

                formatter: (w: any): string => {
                  const totals =
                    w?.globals?.seriesTotals;

                  if (!Array.isArray(totals)) {
                    return '';
                  }

                  return totals
                    .reduce(
                      (sum: number, value: number) =>
                        sum + value,
                      0
                    )
                    .toString();
                }
              }
            }
          }
        }
      }
    };
  }

  private getLabels(): string[] {
    return [
      this.translate.instant('Online'),
      this.translate.instant('Abnormal'),
      this.translate.instant('Offline')
    ];
  }

  private updateTranslations(): void {
    if (!this.chart) {
      return;
    }

    this.chart.updateOptions(
      {
        labels: this.getLabels(),

        plotOptions: {
          pie: {
            donut: {
              size: '72%',

              labels: {
                total: {
                  label:
                    this.translate.instant('Total_Plants')
                }
              }
            }
          }
        },

        tooltip: {
          y: {
            formatter: (value: number): string => {
              const plantLabel =
                value === 1
                  ? this.translate.instant('Plant')
                  : this.translate.instant('Plants');

              return `${value} ${plantLabel}`;
            }
          }
        }
      },
      true,
      true
    );
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.langSub?.unsubscribe();
  }
}